import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Camera, FileText, ShieldCheck, Truck, Wrench, ZoomIn, ChevronRight, Copy, Check } from "lucide-react";

const CATEGORY_LABELS = { camera: "CCTV Cameras", alarm: "Alarm Systems", intercom: "Intercom", dvr: "DVR / NVR", accessory: "Accessories" };

export default function ProductDetailPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [zoom, setZoom] = useState(false);
  const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });
  const [copied, setCopied] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => { (async () => {
    try { const { data } = await api.get(`/products/${id}`); setP(data); setActiveImg(0); } catch { setP(null); }
  })(); }, [id]);

  const onMove = (e) => {
    const rect = imgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPos({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  };

  if (p === null) return <div className="max-w-6xl mx-auto p-16 text-center text-muted-foreground">Product not found.</div>;
  if (!p) return <div className="max-w-6xl mx-auto p-16 text-center text-muted-foreground">Loading…</div>;

  const src = p.images?.[activeImg];

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10">
      <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-4 flex-wrap" aria-label="Breadcrumb" data-testid="product-breadcrumbs">
        <Link to="/" className="hover:text-eagle-gold transition-colors">Home</Link>
        <ChevronRight className="h-3 w-3" />
        <Link to="/products" className="hover:text-eagle-gold transition-colors">Products</Link>
        {p.category && (
          <>
            <ChevronRight className="h-3 w-3" />
            <Link to={`/products?category=${p.category}`} className="hover:text-eagle-gold transition-colors">{CATEGORY_LABELS[p.category] || p.category}</Link>
          </>
        )}
        <ChevronRight className="h-3 w-3" />
        <span className="text-foreground font-medium truncate max-w-[200px]">{p.name}</span>
      </nav>
      <Link to="/products" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8" data-testid="product-back">
        <ArrowLeft className="h-4 w-4" /> Back to catalog
      </Link>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Left: image with hover zoom */}
        <div>
          <div
            ref={imgRef}
            className="relative aspect-square bg-secondary rounded-2xl overflow-hidden flex items-center justify-center mb-4 cursor-zoom-in group"
            onMouseEnter={() => setZoom(true)}
            onMouseLeave={() => setZoom(false)}
            onMouseMove={onMove}
            data-testid="product-image-zoom-container"
          >
            {src ? (
              <>
                <img src={src} alt={p.name} className="w-full h-full object-cover" />
                {zoom && (
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      backgroundImage: `url(${src})`,
                      backgroundSize: "220%",
                      backgroundPosition: `${zoomPos.x}% ${zoomPos.y}%`,
                      backgroundRepeat: "no-repeat",
                    }}
                  />
                )}
                <div className="absolute top-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 opacity-0 group-hover:opacity-100 transition"><ZoomIn className="h-3 w-3" /> Hover to zoom</div>
              </>
            ) : (
              <Camera className="h-24 w-24 text-muted-foreground/30" strokeWidth={1} />
            )}
          </div>
          {p.images?.length > 1 && (
            <div className="grid grid-cols-5 gap-2">
              {p.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} data-testid={`product-thumb-${i}`} className={`aspect-square rounded-lg overflow-hidden border-2 ${activeImg === i ? "border-eagle-gold" : "border-border"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          {/* Below-image benefits panel (previously empty) */}
          <div className="mt-6 grid grid-cols-3 gap-3">
            <div className="p-4 rounded-xl bg-secondary/50 border border-border text-center">
              <Wrench className="h-5 w-5 text-eagle-gold mx-auto mb-2" strokeWidth={1.5} />
              <div className="text-xs font-semibold uppercase tracking-wider">Pro install</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">Licensed technicians</div>
            </div>
            <div className="p-4 rounded-xl bg-secondary/50 border border-border text-center">
              <Truck className="h-5 w-5 text-eagle-gold mx-auto mb-2" strokeWidth={1.5} />
              <div className="text-xs font-semibold uppercase tracking-wider">Brisbane‑wide</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">Same-week install</div>
            </div>
            <div className="p-4 rounded-xl bg-secondary/50 border border-border text-center">
              <ShieldCheck className="h-5 w-5 text-eagle-gold mx-auto mb-2" strokeWidth={1.5} />
              <div className="text-xs font-semibold uppercase tracking-wider">3-yr warranty</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">Full parts & labour</div>
            </div>
          </div>
        </div>

        {/* Right: details */}
        <div>
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <div className="text-xs uppercase tracking-widest text-eagle-gold font-medium">{p.brand_name || "—"}</div>
            {p.subcategory && <Badge variant="outline" className="text-[10px]">{p.subcategory}</Badge>}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-2">{p.name}</h1>
          {p.sku && (
            <button
              onClick={() => { navigator.clipboard.writeText(p.sku); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-eagle-gold transition-colors mb-4"
              data-testid="product-sku-copy"
            >
              SKU: {p.sku} {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
            </button>
          )}
          <div className="flex items-center gap-2 mb-6 text-sm text-eagle-gold font-semibold">
            <ShieldCheck className="h-4 w-4" /> Request a free quote for pricing &amp; availability
          </div>
          <p className="text-muted-foreground leading-relaxed mb-6">{p.description || "No description provided."}</p>

          {p.features?.length > 0 && (
            <Card className="p-6 mb-4">
              <div className="font-display font-semibold mb-4">Key features</div>
              <ul className="space-y-2">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm" data-testid={`product-feature-${i}`}>
                    <span className="text-eagle-gold mt-1 shrink-0">✓</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {p.specifications && Object.keys(p.specifications).length > 0 && (
            <Card className="p-0 mb-4 overflow-hidden">
              <div className="px-6 py-4 border-b border-border bg-secondary/50">
                <div className="font-display font-semibold">Full specifications</div>
                <div className="text-xs text-muted-foreground mt-0.5">{Object.keys(p.specifications).length} attributes</div>
              </div>
              <dl className="divide-y divide-border">
                {Object.entries(p.specifications).map(([k, v], i) => (
                  <div key={k} className={`flex items-center justify-between gap-6 px-6 py-3 ${i % 2 === 1 ? "bg-secondary/20" : ""}`} data-testid={`product-spec-${k}`}>
                    <dt className="text-sm text-muted-foreground shrink-0">{k}</dt>
                    <dd className="text-sm font-semibold text-right">{String(v)}</dd>
                  </div>
                ))}
              </dl>
            </Card>
          )}

          {p.tech_stack && (
            <Card className="p-6 mb-4">
              <div className="font-display font-semibold mb-2">Compatibility &amp; tech</div>
              <p className="text-sm text-muted-foreground">{p.tech_stack}</p>
            </Card>
          )}

          {/* User manual section */}
          <Card className="p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-display font-semibold flex items-center gap-2"><FileText className="h-4 w-4 text-eagle-gold" /> User manual</div>
                <div className="text-xs text-muted-foreground mt-1">Setup guide, wiring diagrams, warranty info.</div>
              </div>
              {p.user_manual_url ? (
                <a href={p.user_manual_url} target="_blank" rel="noreferrer" data-testid="product-manual-link">
                  <Button variant="outline" className="rounded-full"><FileText className="h-4 w-4 mr-2" />Open manual</Button>
                </a>
              ) : (
                <Badge variant="outline" className="text-xs">Coming soon</Badge>
              )}
            </div>
          </Card>

          <div className="flex flex-wrap gap-3">
            <Link to={`/quote?product=${p.product_id}`}>
              <Button className="rounded-full bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover h-11 px-6" data-testid="product-quote-btn">
                Request Install Quote
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}